<template>
    <TransitionModal>
        <Fixed
            v-if="isOpen"
            @close="close"
        >
            <ModalWithHeader
                :label="title"
                @close="close"
            >
                <AuthForm
                    ref="authForm"
                    :registration-disabled
                    :registration-errors="registrationErrors"
                    :registration-hide-inputs-if-logged
                    :start-with-registration
                    @login="onSubmitLogin"
                    @register="$emit('register', $event)"
                    @login-error="$emit('loginError', $event)"
                >
                    <template #registration-title>
                        {{ title }}
                    </template>

                    <template
                        v-if="content?.description"
                        #registration-description
                    >
                        <!-- eslint-disable vue/no-v-html -->
                        <div
                            class="unscoped-enquiry-form-cms-content"
                            v-html="localizeHrefs(content.description)"
                        ></div>
                    </template>

                    <template #registration-contact>
                        <slot name="registration-contact" />
                    </template>

                    <template #registration-submit>
                        {{ content?.buttonText ?? t('enquiry.form.submit') }}
                    </template>

                    <template #login-title>
                        {{ title }}

                        <span
                            v-if="pendingRedirect"
                            class="existing-account-warning"
                        >
                            It looks like you already have an account. Sign in
                            to continue.
                        </span>
                    </template>
                </AuthForm>
            </ModalWithHeader>
        </Fixed>
    </TransitionModal>
</template>

<script lang="ts" setup>
import type { ErrorCollectionWithTraces } from '@/helpers/errors'
import {
    type CustomerRegistrationResponseFragment,
    type EnquiryFormContentFragment,
    type LoginResponseFailureFragment,
    type LoginResponseSuccessFragment,
} from '@/graphql/api'

import AuthForm from './AuthForm.vue'
import TransitionModal from '@/components/layout/modal/TransitionModal.vue'
import Fixed from '@/components/layout/modal/Fixed.vue'
import ModalWithHeader from '@/components/layout/modal/ModalWithHeader.vue'
import { computed, useTemplateRef } from 'vue'
import { useI18n } from 'vue-i18n'
import { useHrefLocalizer } from '@/router/href-localizer.ts'
import { getRoutingRegion } from '@/plugins/region2.ts'
import { useAuth } from '@/plugins/auth.ts'
import { useRouterNavigation } from '@/plugins/released-urls/useRouterNavigation.ts'
import { useRouter } from 'vue-router'
import { useRegionPlugin } from '@/plugins/region.ts'

const localizeHrefs = useHrefLocalizer()
const { t } = useI18n()
const { startWithRegistration = true, content } = defineProps<{
    isOpen: boolean
    startWithRegistration?: boolean
    registrationErrors: ErrorCollectionWithTraces
    registrationDisabled?: boolean
    registrationHideInputsIfLogged?: boolean
    content: EnquiryFormContentFragment | undefined
}>()

const title = computed(() => content?.title ?? t('enquiry.form.title'))

// Manual means user clicked "Sign in" manually, auto means user was existing and we showed him "Sign in" automatically.
export type LoginType = 'manual' | 'auto'

const emit = defineEmits<{
    close: []
    login: [v: LoginResponseSuccessFragment, LoginType]
    register: [successCallback: () => void]
    loginError: [v: LoginResponseFailureFragment]
}>()

const close = () => {
    emit('close')

    pendingRedirect = undefined
}

const authFormRef = useTemplateRef('authForm')

const { setAuth } = useAuth()
const { routerPush } = useRouterNavigation()
const router = useRouter()
const { regionCode } = useRegionPlugin()

type Redirect = string | { name: string } | undefined | null

let pendingRedirect: Redirect = undefined

defineExpose({
    handleRegistrationResponse: (
        response: CustomerRegistrationResponseFragment | undefined,
        redirect: Redirect | null
    ) => {
        if (response) {
            setAuth(response.login)

            if (!response.login) {
                pendingRedirect = redirect

                authFormRef.value?.switchToLogin?.()

                return
            } else {
                doRedirect(redirect)
            }
        } else {
            doRedirect(redirect)
        }
    },
})

const doRedirect = (redirect: Redirect) => {
    if (!redirect) {
        return
    }

    if (typeof redirect === 'string') {
        const route = router.resolve(redirect)

        route.params['region'] = getRoutingRegion(regionCode.value)
        routerPush(route).catch(() => {})
    } else {
        routerPush(redirect).catch(() => {})
    }
}

const onSubmitLogin = (login: LoginResponseSuccessFragment) => {
    emit('login', login, pendingRedirect ? 'auto' : 'manual')

    doRedirect(pendingRedirect)
}
</script>

<style lang="scss">
.existing-account-warning {
    display: inline-block;
    font-size: 14px;
    color: rgb(102, 60, 0);
    line-height: normal;
    font-family: inherit;
    letter-spacing: 0.01em;
    font-style: normal;
    text-align: center;
    background-color: rgb(255, 244, 229);
    padding: 12px 20px;
    border-radius: 8px;
    border: 1px solid rgb(255, 226, 185);
    margin: 10px auto;
    width: 100%;
    box-sizing: border-box;
    font-weight: 500;
}

.unscoped-enquiry-form-cms-content {
    font-size: 1rem;
    line-height: 1.4em;
    font-family: $sectra-font-stack;

    strong,
    b {
        font-weight: bold;
    }

    em,
    i {
        font-style: italic;
    }

    p {
        font-size: 1.125rem;
        margin-bottom: 15px;
    }

    ul,
    ol {
        background-color: $sage;
        border-radius: var(--input-border-radius);
        padding: var(--spacing-16) var(--spacing-20i) var(--spacing-16)
            var(--spacing-40);
        font-size: 1rem;
        max-width: 340px;
        margin: var(--spacing-16) auto;
        text-align: left;
    }

    ul {
        list-style: disc outside;
    }

    ol {
        list-style: decimal outside;
    }
}
</style>
